import React, {useEffect, useRef, useState} from 'react';
import {
  getNotificationStatus,
  getNotificationFlag,
  cancelBackgroundTask,
} from '../../../../../api/endpoints/reportingApi';
import ArrWaitToCompleteModal from '../../../../common/arr-wait-to-complete-modal/ArrWaitToCompleteModal';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {t} from 'i18next';
import ArrToast from '../../../../common/arr-toast/ArrToast';
import {ReactComponent as WarningIcon} from '../../../../../assets/images/warning-toast.svg';
import {ReactComponent as CloseToast} from '../../../../../assets/images/close-toast.svg';
import {ReactComponent as SuccessIcon} from '../../../../../assets/images/success-toast.svg';
import {ReactComponent as ErrorIcon} from '../../../../../assets/images/error-toast.svg';

interface PollingWrapperProps {
  notificationId: number;
  reportId: number;
  timeout?: number;
  interval?: number;
  maxPolling?: number;
  onComplete: (reportId: number) => void;
  onFlag?: (notificationFlag: boolean) => void;
  loader?: boolean;
  showClose?: boolean;
}

enum ToastType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

const ArrWaitToCompleteModalWrapper: React.FC<PollingWrapperProps> = ({
  notificationId,
  reportId,
  timeout = 9000,
  interval = 2000,
  maxPolling = 30,
  onComplete,
  onFlag,
  loader = true,
  showClose = true,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showAgain, setShowAgain] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [loading, setLoading] = useState(false);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const modalRef = useRef<NodeJS.Timeout | null>(null);
  const completedRef = useRef(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Info);

  const [pollCounter, setPollCounter] = useState(0);

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const startPolling = () => {
    if (
      !notificationId ||
      notificationId <= 0 ||
      !reportId ||
      reportId <= 0 ||
      completedRef.current
    )
      return;

    setLoading(true);

    pollingRef.current = setInterval(async () => {
      if (completedRef.current) return;

      try {
        checkMaxPolling();
        const response = await getNotificationStatus(notificationId);
        if (response.status.toLowerCase() === 'completed') {
          completedRef.current = true;
          stopPolling();
          setShowModal(false);
          setLoading(false);
          setDoNotShowAgain(false);
          onComplete(reportId);
        } else if (response.status.toLowerCase() === 'error') {
          setShowModal(false);
          setLoading(false);
          setDoNotShowAgain(false);
          stopPolling();
          onFlag?.(true);
          triggerToast('Something went wrong.', ToastType.Error);
        }
      } catch (error: any) {
        stopPolling();
        setShowModal(false);
        setLoading(false);
        triggerToast(
          error?.response?.data?.title ?? 'Something went wrong.',
          ToastType.Error
        );
      }
    }, interval);

    if (!doNotShowAgain) {
      modalRef.current = setInterval(() => {
        if (!completedRef.current) {
          stopPolling();
          setShowModal(true);
        }
      }, timeout);
    }
  };

  const checkMaxPolling = () => {
    setPollCounter((prev) => {
      const next = prev + 1;
      return next <= maxPolling ? next : prev;
    });
  };

  const stopPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (modalRef.current) clearInterval(modalRef.current);
    pollingRef.current = null;
    modalRef.current = null;
    setLoading(false);
  };

  useEffect(() => {
    completedRef.current = false;
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (modalRef.current) clearInterval(modalRef.current);
    pollingRef.current = null;
    modalRef.current = null;
    // Reset to initial when polling start again
    setDoNotShowAgain(false);
    setShowAgain(false);
    setPollCounter(0);
    startPolling();
    return () => stopPolling();
  }, [notificationId, reportId]);

  useEffect(() => {
    if (pollCounter >= maxPolling) {
      triggerToast(t('domainDashboard.exportReportSuccess'), ToastType.Warning);
      handleNo();
    }
  }, [pollCounter]);

  const handleYes = () => {
    setShowModal(false);
    setShowAgain(true);

    if (doNotShowAgain) {
      setLoading(true);
      modalRef.current = null;
      pollingRef.current = setInterval(async () => {
        if (completedRef.current) return;

        try {
          checkMaxPolling();
          const response = await getNotificationStatus(notificationId);
          if (response.status.toLowerCase() === 'completed') {
            completedRef.current = true;
            stopPolling();
            setLoading(false);
            onComplete(reportId);
          } else if (response.status.toLowerCase() === 'error') {
            setShowModal(false);
            setLoading(false);
            stopPolling();
            onFlag?.(true);
            triggerToast('Something went wrong.', ToastType.Error);
          }
        } catch (error: any) {
          stopPolling();
          setLoading(false);
          triggerToast(
            error?.response?.data?.title ?? 'Something went wrong.',
            ToastType.Error
          );
        }
      }, interval);
    } else {
      startPolling();
    }
  };

  const handleNo = async () => {
    stopPolling();
    setShowModal(false);
    setLoading(false);
    setDoNotShowAgain(false);
    try {
      onFlag?.(true);
      await getNotificationFlag(reportId, notificationId);
    } catch (error: any) {
      triggerToast(
        error?.response?.data?.title ?? 'Something went wrong.',
        ToastType.Error
      );
    }
  };

  const handleCancel = async () => {
    stopPolling();
    setShowModal(false);
    setLoading(false);
    try {
      onFlag?.(true);
      await cancelBackgroundTask(reportId, notificationId);
    } catch (error: any) {
      triggerToast(
        error?.response?.data?.title ?? 'Something went wrong.',
        ToastType.Error
      );
    }
  };

  const getStartIconAndTitle = (type: ToastType) => {
    switch (type) {
      case ToastType.Success:
        return {icon: <SuccessIcon />, title: 'Success'};
      case ToastType.Error:
        return {icon: <ErrorIcon />, title: 'Error'};
      case ToastType.Warning:
        return {icon: <WarningIcon />, title: ''};
      default:
        return {icon: <WarningIcon />, title: ''};
    }
  };

  const {icon, title} = getStartIconAndTitle(toastStatus);

  return (
    <>
      {loader && loading && (
        <div
          style={{display: 'flex', justifyContent: 'center', marginTop: '50px'}}
          data-testid={`arr-wait-to-complete-modal-wrapper-loading`}
        >
          <ArrLoadingSpinner show={loading} overlay={true} isOpen={true} />
        </div>
      )}
      {showModal && (
        <ArrWaitToCompleteModal
          showModal={showModal}
          showAgain={showAgain}
          doNotShowAgain={doNotShowAgain}
          showClose={showClose}
          setDoNotShowAgain={setDoNotShowAgain}
          onYes={handleYes}
          onCloseNo={handleNo}
          onCancel={handleCancel}
          onOutsideClick={handleYes}
        />
      )}
      {showToast && (
        <ArrToast
          id="status-toast"
          startIcon={icon}
          title={title}
          showStartIcon={true}
          type={toastStatus}
          message={toastMessage}
          duration={5000}
          dismissable
          onClose={() => setShowToast(false)}
          position="bottom-right"
          closeIcon={<CloseToast />}
        />
      )}
    </>
  );
};

export default ArrWaitToCompleteModalWrapper;
